import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import type { QuestionScope } from "@/hera-api/client/types.gen";
import { useQuestionsForm } from "@/hooks/use-question-form";
import { isGovernmentPortalViewer } from "@/lib/government-impersonate";
import {
  getQueryErrorHeading,
  getQueryErrorMessage,
  getUserType,
} from "@/lib/utils";
import { useAuth0 } from "@auth0/auth0-react";
import ErrorComponent from "../ui/error";
import LoadingScreen from "../ui/loading-screen";
import { QuestionForm } from "./question-form";

// Type for simplified scopes used in the UI
type SimplifiedScope = "global" | "group" | "jurisdiction";

interface QuestionDetailsSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  scope: SimplifiedScope | null;
  questionId: string | null;
  duplicateQuestionData?: any;
  restrictToInspectionScopes?: boolean;
  restrictToDeregistrationScopes?: boolean;
  /** Force the sheet title and description into view-only mode. */
  readOnly?: boolean;
  /** Dashboard jurisdiction filter at open time (create defaults). */
  filterJurisdictionIds?: string[];
  /** Dashboard license type filter at open time (create defaults). */
  filterLicenseTypeIds?: string[];
}

export function QuestionDetailsSheet({
  isOpen,
  onOpenChange,
  scope,
  questionId,
  duplicateQuestionData,
  restrictToInspectionScopes = false,
  restrictToDeregistrationScopes = false,
  readOnly: readOnlyProp = false,
  filterJurisdictionIds = [],
  filterLicenseTypeIds = [],
}: QuestionDetailsSheetProps) {
  const { user } = useAuth0();
  const userType = getUserType(user);
  const isGovernment = isGovernmentPortalViewer(userType);

  // Convert simplified scope to backend scope for the hook
  const getBackendScope = (
    simplifiedScope: SimplifiedScope | null,
  ): QuestionScope | null => {
    if (!simplifiedScope) return null;
    return simplifiedScope;
  };

  const questionType = restrictToDeregistrationScopes
    ? ("deregistration" as const)
    : restrictToInspectionScopes
      ? ("inspection" as const)
      : ("registration" as const);

  const backendScope = getBackendScope(scope);

  const {
    questionData,
    jurisdictionsData,
    licenseTypesData,
    isLoading,
    error,
    handleSubmit,
  } = useQuestionsForm(backendScope, questionId, questionType);

  const handleCancel = () => {
    onOpenChange(false);
  };

  const getSimplifiedScope = (backendScope: QuestionScope): SimplifiedScope =>
    backendScope;

  // Use duplicateQuestionData if present and adding a new question
  // For new questions (not duplicating), use null to avoid inheriting old data
  const effectiveQuestionData =
    !questionId && duplicateQuestionData
      ? duplicateQuestionData
      : questionId
        ? questionData
        : null;

  // If duplicating, determine the appropriate scope
  // For government users, always convert global to jurisdiction-specific
  // Otherwise, use the passed scope (which should be set correctly for new questions)
  const effectiveScope: SimplifiedScope | null =
    !questionId && duplicateQuestionData
      ? isGovernment
        ? "jurisdiction"
        : getSimplifiedScope(duplicateQuestionData.scope)
      : scope;

  // Determine if this is a global question for government users
  const isGlobalQuestionForGovernment =
    questionId &&
    questionData &&
    isGovernment &&
    getSimplifiedScope(questionData.scope) === "global";

  // Determine the question type label based on panel context
  const questionTypeLabel = restrictToDeregistrationScopes
    ? "De-registration"
    : restrictToInspectionScopes
      ? "Inspection"
      : "Registration";

  const showAsViewOnly = readOnlyProp || Boolean(isGlobalQuestionForGovernment);

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>
            {!questionId && !showAsViewOnly
              ? `Add New ${questionTypeLabel} Question`
              : showAsViewOnly
                ? `View ${questionTypeLabel} Question`
                : `Edit ${questionTypeLabel} Question`}
          </SheetTitle>
          <SheetDescription>
            {!questionId && !showAsViewOnly
              ? `Create a new ${questionTypeLabel.toLowerCase()} question.`
              : showAsViewOnly
                ? "View question details."
                : `Edit an existing ${questionTypeLabel.toLowerCase()} question.`}
          </SheetDescription>
        </SheetHeader>

        <div className="overflow-y-auto max-h-[calc(100vh-120px)] px-4">
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <LoadingScreen />
            </div>
          ) : error ? (
            <ErrorComponent
              title={getQueryErrorHeading(error)}
              message={getQueryErrorMessage(error)}
            />
          ) : (
            <QuestionForm
              questionId={questionId}
              scope={effectiveScope}
              questionData={effectiveQuestionData}
              jurisdictions={jurisdictionsData}
              licenseTypes={licenseTypesData}
              restrictToInspectionScopes={restrictToInspectionScopes}
              restrictToDeregistrationScopes={restrictToDeregistrationScopes}
              userType={userType ?? undefined}
              filterJurisdictionIds={filterJurisdictionIds}
              filterLicenseTypeIds={filterLicenseTypeIds}
              onSubmit={async (data) => {
                try {
                  // Form already converts simplified scope to backend scope
                  await handleSubmit(data);
                  onOpenChange(false);
                } catch (error) {
                  // Error is already handled with toast in the hook
                }
              }}
              onCancel={showAsViewOnly ? handleCancel : handleCancel}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
